import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SelectMetaField } from '@ge/shared/components/meta-fields/select-meta-field';

import { useComponentHierarchy } from '../../../data-hooks';

const FormSchema = {
  component1: 'component1',
  component2: 'component2',
  component3: 'component3',
};

const Select = styled(SelectMetaField)`
  word-wrap: break-word;
  text-transform: capitalize;
  width: 162px;
  .select__dropdown-control {
    display: flex;
    span {
      overflow: hidden;
      flex: 1;
      text-overflow: ellipsis;
    }
  }
`;

export const AssetComponents = ({ assetIds, className, width, task, requiredL1 }) => {
  const { t, ready } = useTranslation(['tasks'], { useSuspense: false });

  const { data: componentHierarchy } = useComponentHierarchy(assetIds);
  const { getComponentTypeDescription } = useStoreState((state) => state.tenant);

  const { watch, setValue } = useFormContext();
  const component1Value = watch(FormSchema.component1, false);
  const component2Value = watch(FormSchema.component2, false);

  const component10LabelDescription = (getComponentTypeDescription, c) => {
    const getLabels = getComponentTypeDescription?.find(
      (r) => r?.componentTypeId === c?.type,
    )?.description;
    return getLabels;
  };

  const component20LabelDescription = (getComponentTypeDescription, child) => {
    const getLabels = getComponentTypeDescription?.find(
      (r) => r?.componentTypeId === child?.type,
    )?.description;
    return getLabels;
  };

  const component30LabelDescription = (getComponentTypeDescription, child) => {
    const getLabels = getComponentTypeDescription?.find(
      (r) => r?.componentTypeId === child?.type,
    )?.description;
    return getLabels;
  };

  const component1Options = useMemo(() => {
    const data = [];
    componentHierarchy?.forEach((c) => {
      const componentDescription = component10LabelDescription(getComponentTypeDescription, c);
      componentDescription &&
        data.push({
          value: `${c?.type}~${c?.componentTypeNumber}`,
          label: `${componentDescription} ${c?.componentTypeNumber}`,
        });
    });
    return data;
  }, [componentHierarchy, getComponentTypeDescription]);

  const component2Options = useMemo(() => {
    const data = [];
    componentHierarchy
      ?.find((c) => c?.type === component1Value?.toString().split('~')[0])
      ?.children?.forEach((child) => {
        const componentDescription = component20LabelDescription(
          getComponentTypeDescription,
          child,
        );
        componentDescription &&
          data.push({
            value: `${child?.type}~${child?.componentTypeNumber}`,
            label: `${componentDescription} ${child?.componentTypeNumber}`,
          });
      });
    return data;
  }, [componentHierarchy, getComponentTypeDescription, component1Value]);

  const component3Options = useMemo(() => {
    const data = [];
    componentHierarchy
      ?.find((c) => c?.type === component1Value?.toString().split('~')[0])
      ?.children?.find((c) => c?.type === component2Value?.toString().split('~')[0])
      ?.children?.forEach((child) => {
        const componentDescription = component30LabelDescription(
          getComponentTypeDescription,
          child,
        );
        componentDescription &&
          data.push({
            value: `${child?.type}~${child?.componentTypeNumber}`,
            label: `${componentDescription} ${child?.componentTypeNumber}`,
          });
      });
    return data;
  }, [componentHierarchy, component1Value, component2Value, getComponentTypeDescription]);

  const meta = useMemo(
    () => ({
      [FormSchema.component1]: { required: !!requiredL1 },
      [FormSchema.component2]: {
        readOnly: !component1Value,
      },
      [FormSchema.component3]: {
        readOnly: !component1Value || !component2Value,
      },
    }),
    [requiredL1, component1Value, component2Value],
  );

  const { component1 = '', component2 = '', component3 = '' } = useMemo(() => task ?? {}, [task]);

  if (!ready) {
    return null;
  }

  return (
    <div className={className}>
      <Select
        label={`${t('form.component', 'Component')} L1 ${requiredL1 ? '*' : ''}`}
        defaultValue={component1}
        metadata={meta[FormSchema.component1]}
        maxWidth={width}
        minWidth={width}
        name={FormSchema.component1}
        placeholder={`${t('component', 'Component')} 1`}
        options={component1Options}
        handleChange={() => {
          setValue('component2', '');
          setValue('component3', '');
        }}
      />
      <Select
        label={`${t('form.component', 'Component')} L2`}
        defaultValue={component2}
        metadata={meta[FormSchema.component2]}
        maxWidth={width}
        minWidth={width}
        name={FormSchema.component2}
        placeholder={`${t('component', 'Component')} 2`}
        options={component2Options}
        handleChange={() => {
          setValue('component3', '');
        }}
      />
      <Select
        label={`${t('form.component', 'Component')} L3`}
        defaultValue={component3}
        metadata={meta[FormSchema.component3]}
        maxWidth={width}
        minWidth={width}
        name={FormSchema.component3}
        placeholder={`${t('component', 'Component')} 3`}
        options={component3Options}
      />
    </div>
  );
};

AssetComponents.propTypes = {
  assetIds: PropTypes.array,
  className: PropTypes.string,
  width: PropTypes.number,
  reset: PropTypes.bool,
  task: PropTypes.instanceOf(Object),
  requiredL1: PropTypes.bool,
};

AssetComponents.defaultProps = {
  assetIds: [],
  className: null,
  width: null,
  reset: false,
  task: null,
  requiredL1: false,
};
